import type {
  AdminAgencyObligationOverlayAgency,
  AgencyObligationOverlayCatalogOption,
  AgencyObligationOverlayHistoryView,
  AgencyObligationOverlayView,
  AgencyObligationOverlayWriteResult,
  ApiResponse,
  CreateAgencyObligationOverlayBody,
  UpdateAgencyObligationOverlayBody,
} from '@safetyhub/shared';
import api from './api';

/**
 * admin **기관 서식 변형(오버레이)** API — 서버 `AdminLegalObligationOverlaysController`
 * (`admin/legal-obligation-overlays` · 권한 `legal-obligation-catalog`). 계약 정본은 shared `legal-obligation-overlays.ts`.
 */

function unwrapEnvelope<T>(envelope: ApiResponse<T>): T {
  if (envelope.data === null) {
    throw new Error('서버 응답에 본문이 없습니다. 잠시 후 다시 시도해 주세요.');
  }
  return envelope.data;
}

const BASE = '/admin/legal-obligation-overlays';

export const LEGAL_OBLIGATION_OVERLAY_QUERY_KEYS = {
  agencies: ['admin', 'legal-obligation-overlays', 'agencies'] as const,
  /** 접두 — 아래 둘을 함께 무효화한다 */
  all: ['admin', 'legal-obligation-overlays'] as const,
  list: (agencyCompanyId: string) => ['admin', 'legal-obligation-overlays', 'list', agencyCompanyId] as const,
  history: (overlayId: string) => ['admin', 'legal-obligation-overlays', 'history', overlayId] as const,
};

export interface AdminAgencyOverlayListResponse {
  overlays: AgencyObligationOverlayView[];
  catalog: AgencyObligationOverlayCatalogOption[];
}

export const legalObligationOverlaysApi = {
  async agencies(): Promise<AdminAgencyObligationOverlayAgency[]> {
    const res = await api.get<ApiResponse<AdminAgencyObligationOverlayAgency[]>>(`${BASE}/agencies`);
    return unwrapEnvelope(res.data);
  },

  async list(agencyCompanyId: string): Promise<AdminAgencyOverlayListResponse> {
    const res = await api.get<ApiResponse<AdminAgencyOverlayListResponse>>(BASE, {
      params: { agencyCompanyId },
    });
    return unwrapEnvelope(res.data);
  },

  async create(
    agencyCompanyId: string,
    body: CreateAgencyObligationOverlayBody,
  ): Promise<AgencyObligationOverlayWriteResult> {
    const res = await api.post<ApiResponse<AgencyObligationOverlayWriteResult>>(BASE, {
      ...body,
      agencyCompanyId,
    });
    return unwrapEnvelope(res.data);
  },

  async update(
    overlayId: string,
    body: UpdateAgencyObligationOverlayBody,
  ): Promise<AgencyObligationOverlayWriteResult> {
    const res = await api.patch<ApiResponse<AgencyObligationOverlayWriteResult>>(`${BASE}/${overlayId}`, body);
    return unwrapEnvelope(res.data);
  },

  async history(overlayId: string): Promise<AgencyObligationOverlayHistoryView[]> {
    const res = await api.get<ApiResponse<AgencyObligationOverlayHistoryView[]>>(`${BASE}/${overlayId}/history`);
    return unwrapEnvelope(res.data);
  },
};
